/**
 * Custom rendition menu that only shows preferred codec levels
 * This replaces the default media-rendition-menu to show only filtered levels
 */

import { MediaRenditionMenu } from 'media-chrome/menu/media-rendition-menu.js';
import type { Rendition } from 'media-chrome/media-store/state-mediator.js';
import { MediaUIAttributes } from 'media-chrome/constants.js';
import { getNumericAttr } from 'media-chrome/utils/element-utils.js';
import { findStandardResolution } from '../utils/resolution-utils.ts';

interface FilteredLevel {
  level?: number;
  originalIndex?: number;
  /** Rendition id assigned by the video element; wins over originalIndex. */
  renditionId?: string;
  width: number;
  height: number;
  bitrate: number;
}

/**
 * Applies standard resolution rounding to a rendition for display.
 * Only rounds when both dimensions are present and > 0 to avoid "0p" labels.
 */
function toDisplayRendition(rendition: Rendition): Rendition {
  const { width, height } = rendition;

  // Only apply rounding when both dimensions are present and > 0
  if (
    typeof width === 'number' &&
    width > 0 &&
    typeof height === 'number' &&
    height > 0
  ) {
    const displayResolution = findStandardResolution(width, height);
    return {
      ...rendition,
      width: displayResolution,
      height: displayResolution,
    };
  }

  // If one or both dimensions are missing or non-positive, leave unchanged
  return rendition;
}

class BunnyRenditionMenu extends MediaRenditionMenu {
  static get observedAttributes(): string[] {
    return [...super.observedAttributes, MediaUIAttributes.MEDIA_WIDTH];
  }

  #filteredLevels: FilteredLevel[] = [];

  // Override mediaHeight to return rounded shorter dimension (fixes Auto label for vertical video)
  override get mediaHeight(): number {
    const height = super.mediaHeight;
    const width = getNumericAttr(this, MediaUIAttributes.MEDIA_WIDTH) ?? 0;
    if (width > 0 && height > 0) {
      // findStandardResolution uses Math.min internally, handles both orientations
      return findStandardResolution(width, height);
    }
    // Fallback: return height as-is when width unavailable (can't determine orientation)
    return height;
  }

  override set mediaHeight(value: number) {
    super.mediaHeight = value;
  }

  set filteredLevels(levels: FilteredLevel[]) {
    this.#filteredLevels = levels;
    // Trigger re-render when filtered levels are set
    this.requestUpdate?.();
  }

  // Override mediaRenditionList getter to apply standard resolution rounding
  get mediaRenditionList(): Rendition[] {
    if (this.#filteredLevels.length > 0) {
      return this.#filteredLevels.map((level, index) =>
        toDisplayRendition({
          id: level.renditionId ?? level.originalIndex?.toString() ??
            `filtered-${index}`,
          width: level.width,
          height: level.height,
          bitrate: level.bitrate,
        })
      );
    }
    return super.mediaRenditionList.map(toDisplayRendition);
  }

  set mediaRenditionList(value: Rendition[]) {
    // Accept the value to prevent errors, but use filtered levels if available
    if (this.#filteredLevels.length === 0) {
      super.mediaRenditionList = value;
    }
  }
}

if (!globalThis.customElements.get('bunny-rendition-menu')) {
  globalThis.customElements.define('bunny-rendition-menu', BunnyRenditionMenu);
}

export default BunnyRenditionMenu;
